/* eslint-disable react/require-default-props */
import { BadgePill } from '@brandfolder/react';
import { t } from '@lingui/macro';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import Checkbox from '@components/common/checkbox/main';
import { customTemplatesVisitable } from '@components/design_huddles/editors/helpers';
import { BrandfolderHexColors } from '@components/library/utils';

import { ViewOnlyIcon } from '@components/view-only/ViewOnlyIcon';

import AssetActionsMenu from './asset_actions_menu';

import './styles/_asset_actions_menu.scss';

const CardListView = ({
  actionsMenuItemsShown,
  activeLabel,
  allowActionsMenu,
  asset,
  connectDragSource,
  createAndClickLink,
  customFieldsKeys,
  handleOpenModal,
  handleRemoveSelectedAssetFromSet,
  handleSelectionClick,
  filtersDrawerOpen,
  getBannerInfo,
  listViewTableSpec,
  onVisitAsset,
  postActionsSlot,
  renderAssetThumbnail,
  reorderable,
  section,
  selected,
  downloadRequestId,
  task,
  windowDimensions,
  showCustomFields,
  showTags,
}) => {
  const [showMoreTags, setShowMoreTagsCopy] = useState(false);
  const [showFade, setShowFade] = useState(false);
  const [showMoreTagsCopy, setMoreTagsCopy] = useState('');
  const tagsList = useRef();
  const {
    attachment_count,
    extension,
    printui_editor_link,
    prioritized_custom_fields,
    storyteq_editor_link,
    design_huddle_editor_link,
    content_automation_editor_link,
    name,
    tag_names,
    type,
    view_only
  } = asset.attributes;

  const hideAttachmentInfo = (extension === "empty" && attachment_count < 1)
    || type === "Text"
    || type === "texts";

  const attachmentCopy = (printui_editor_link || storyteq_editor_link || design_huddle_editor_link || content_automation_editor_link)
    ? <span className="bff-template-editor" />
    : <span>{extension}</span>;

  const renderMoreTagsCopy = () => {
    const tagNameSpacing = 6; // must match scss variable $tagNameSpacing (margin-right style)
    let tagsElementsWidth = 0;
    let tagsCount = 0;
    let hiddenTagsCount = 0;

    if (tagsList.current) {
      const tagsListWidth = tagsList.current.offsetWidth;
      const tagsHTMLCollection = tagsList.current.children; // 'array-like' node list of li elements
      tagsCount = tagsHTMLCollection.length;

      for (let i = 0; i < tagsCount; i += 1) {
        tagsElementsWidth += (tagsHTMLCollection[i].offsetWidth + tagNameSpacing);

        hiddenTagsCount = (tagsElementsWidth > tagsListWidth) && hiddenTagsCount === 0
          ? tagsCount - (i + 1)
          : hiddenTagsCount;
      }

      setShowMoreTagsCopy(tagsElementsWidth > tagsListWidth && hiddenTagsCount > 0);
      setShowFade(tagsElementsWidth > tagsListWidth);
      setMoreTagsCopy(hiddenTagsCount <= 99 ? t`+ ${hiddenTagsCount} more` : t`99+`);
    }
  };

  const prioritizedCustomFieldValues = (keyName) => {
    const keyValueObject = prioritized_custom_fields?.find((fields) => (
      fields.key.name === keyName
    ));

    if (keyValueObject?.values.length) {
      return keyValueObject.values.map(({ value }) => value).join(', ');
    }

    return '-';
  };

  useEffect(() => {
    const updateMoreTags = () => {
      if (tagsList && tagsList.current) {
        renderMoreTagsCopy();
      } else {
        setShowMoreTagsCopy(false);
        setMoreTagsCopy('');
      }
    };

    setTimeout(updateMoreTags, 500); // delay allows element dimensions to update, e.g. when advanced filters drawer slides open
  }, [tagsList, filtersDrawerOpen, windowDimensions, showCustomFields]);

  const { color, copy, displayType } = getBannerInfo();
  const visitableCustomTemplateAsset = design_huddle_editor_link && customTemplatesVisitable();

  const viewOnlyAssetsEnabled = BFG.hasFeature('view_only_assets');

  const spec = listViewTableSpec || {
    showReorder: reorderable,
    customFieldKeys: showCustomFields ? customFieldsKeys : [],
    showTagsColumn: showTags && !showCustomFields,
    showTagsPlaceholder: !showTags && !showCustomFields,
    showViewOnlyColumn: viewOnlyAssetsEnabled,
  };

  const reorderCell = spec.showReorder && (
    <td key="reorder" className="list-td list-td-reorder">
      <div className="reorder-container">
        {connectDragSource(
          <button
            className="asset-reorder button tertiary"
            type="button"
          >
            <span aria-label="Reorder" className="bff-reorder" />
          </button>
        )}
      </div>
    </td>
  );

  const thumbCell = (
    <td key="thumb" className="list-td list-td-thumb">
      <div
        className="list-asset-card j-resource-card"
        onClick={(e) => {
          if (content_automation_editor_link || storyteq_editor_link || printui_editor_link || visitableCustomTemplateAsset || type === "Press") {
            e.stopPropagation();
            createAndClickLink();
          }
        }}
      >
        {renderAssetThumbnail()}
      </div>
    </td>
  );

  const nameCell = (
    <td key="name" className="list-td list-td-name">
      <p className="list-asset-name asset-name">{name}</p>
    </td>
  );

  const badgeCell = (
    <td key="badge" className="list-td list-td-badge">
      {!!color && !!copy && !!displayType && (
        <BadgePill
          backgroundColor={color}
          className={`status-ribbon ${displayType}`}
          fontColor={color === BrandfolderHexColors.Error600 ? BrandfolderHexColors.White : undefined}
          size='small'
        >
          {copy}
        </BadgePill>
      )}
    </td>
  );

  const attachmentCell = (
    <td key="attachment" className="list-td list-td-attachment">
      {!hideAttachmentInfo && (
        <div className={viewOnlyAssetsEnabled && view_only && !downloadRequestId ? 'list-attachment-info-padding' : 'list-attachment-info'}>
          <div className="file-type-pill">
            {attachment_count > 1
              ? (
                <>
                  <span className="bff-paperclip" />
                  <span>{attachment_count > 100 ? '+' : attachment_count }</span>
                </>
              )
              : attachmentCopy
            }
          </div>
        </div>
      )}
    </td>
  );

  const viewOnlyCell = spec.showViewOnlyColumn && (
    <td key="view-only" className="list-td list-td-view-only">
      {viewOnlyAssetsEnabled && view_only && !downloadRequestId && (
        <div className="list-view-only-icon">
          <ViewOnlyIcon />
        </div>
      )}
    </td>
  );

  const customFieldCells = (showCustomFields && spec.customFieldKeys?.length > 0)
    ? spec.customFieldKeys.map((key, i) => (
      <td
        key={`cf-${asset.id}-${key}-${i}`} // eslint-disable-line react/no-array-index-key
        className="list-td list-td-custom-field"
      >
        <p
          className="list-asset-custom-field"
          title={prioritizedCustomFieldValues(key)}
        >
          {prioritizedCustomFieldValues(key)}
        </p>
      </td>
    ))
    : null;

  const tagsColumnCell = spec.showTagsColumn ? (
    <td key="tags" className="list-td list-td-tags">
      {showTags ? (
        <>
          <ul
            ref={tagsList}
            className={classnames(
              'list-asset-tags',
              { 'list-show-more-tags': showMoreTags },
              { 'overflow-fade': showFade },
            )}
          >
            {tag_names.length
              ? (tag_names.map((tag) => (<li key={tag} className="tag-name">{tag}</li>)))
              : (<p>-</p>)
            }
          </ul>
          {showMoreTags && (<p className="list-show-more-tags-copy">{showMoreTagsCopy}</p>)}
        </>
      ) : (
        <div className="list-asset-tags-placeholder" />
      )}
    </td>
  ) : (spec.showTagsPlaceholder ? (
    <td key="tags-placeholder" className="list-td list-td-tags">
      <div className="list-asset-tags-placeholder" />
    </td>
  ) : null);

  const customOrTagsCells = (
    <>
      {customFieldCells}
      {tagsColumnCell}
    </>
  );

  const actionsCell = (
    <td key="actions" className="list-td list-td-actions list-asset-actions">
      {allowActionsMenu && (
        <div
          aria-label="More options"
          className="action-menu"
          onClick={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        >
          <AssetActionsMenu
            actionsMenuItemsShown={actionsMenuItemsShown}
            activeLabel={activeLabel}
            asset={asset}
            handleOpenModal={handleOpenModal}
            handleRemoveSelectedAssetFromSet={handleRemoveSelectedAssetFromSet}
            onVisitAsset={onVisitAsset}
            section={section}
            task={task?.attributes}
          />
        </div>
      )}
      {postActionsSlot}
    </td>
  );

  const checkboxCell = (
    <td key="checkbox" className="list-td list-td-checkbox">
      <div className="list-asset-checkbox">
        <Checkbox
          ariaLabel={`Select ${name}`}
          checked={selected}
          size="sm"
          toggle={handleSelectionClick}
        />
      </div>
    </td>
  );

  return (
    <>
      {reorderCell}
      {thumbCell}
      {checkboxCell}
      {nameCell}
      {badgeCell}
      {attachmentCell}
      {viewOnlyCell}
      {customOrTagsCells}
      {actionsCell}
    </>
  );
};

CardListView.propTypes = {
  actionsMenuItemsShown: PropTypes.shape({
    canEdit: PropTypes.bool,
    canShare: PropTypes.bool,
    canAnalyze: PropTypes.bool,
    canDelete: PropTypes.bool,
    canRemove: PropTypes.bool,
    canUnlabel: PropTypes.bool,
    canDownload: PropTypes.bool,
    canOpen: PropTypes.bool,
  }).isRequired,
  activeLabel: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  }),
  allowActionsMenu: PropTypes.bool.isRequired,
  asset: PropTypes.shape({
    attributes: PropTypes.shape({
      attachment_count: PropTypes.number,
      extension: PropTypes.string,
      name: PropTypes.string,
      printui_editor_link: PropTypes.string,
      prioritized_custom_fields: PropTypes.arrayOf(PropTypes.shape({
        keys: PropTypes.shape({
          name: PropTypes.string,
          prioritized: PropTypes.bool,
          multi_value_enabled: PropTypes.bool
        }),
        values: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
      })),
      storyteq_editor_link: PropTypes.string,
      design_huddle_editor_link: PropTypes.string,
      content_automation_editor_link: PropTypes.string,
      tag_names: PropTypes.arrayOf(PropTypes.string),
      type: PropTypes.string,
      view_only: PropTypes.bool
    }),
    id: PropTypes.string,
  }).isRequired,
  connectDragSource: PropTypes.func.isRequired,
  createAndClickLink: PropTypes.func.isRequired,
  customFieldsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  listViewTableSpec: PropTypes.shape({
    showReorder: PropTypes.bool,
    customFieldKeys: PropTypes.arrayOf(PropTypes.string),
    showTagsColumn: PropTypes.bool,
    showTagsPlaceholder: PropTypes.bool,
    showViewOnlyColumn: PropTypes.bool,
  }),
  postActionsSlot: PropTypes.node,
  filtersDrawerOpen: PropTypes.string.isRequired,
  getBannerInfo: PropTypes.func.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  handleRemoveSelectedAssetFromSet: PropTypes.func.isRequired,
  handleSelectionClick: PropTypes.func.isRequired,
  onVisitAsset: PropTypes.func,
  renderAssetThumbnail: PropTypes.func.isRequired,
  reorderable: PropTypes.bool.isRequired,
  section: PropTypes.shape({
    key: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  downloadRequestId: PropTypes.number,
  showCustomFields: PropTypes.bool,
  showTags: PropTypes.bool.isRequired,
  task: PropTypes.shape({
    attributes: PropTypes.shape({})
  }),
  windowDimensions: PropTypes.shape({
    innerHeight: PropTypes.number,
    innerWidth: PropTypes.number,
  })
};

CardListView.defaultProps = {
  activeLabel: null,
  downloadRequestId: null,
  listViewTableSpec: undefined,
  onVisitAsset: () => {},
  postActionsSlot: null,
  showCustomFields: false,
  task: undefined,
  windowDimensions: {},
};

export default CardListView;
